import { memo, useState, useRef, useEffect } from 'react';
import { getInsuranceTypeName } from '../../../utils/insuranceTypeUtils';

/**
 * 保険会社のポップアップ選択コンポーネント
 *
 * 生損保区分でフィルタリングされた保険会社一覧を表示し、複数選択をサポートする。
 *
 * @param {object} props
 * @param {number} props.insuranceType - 生損保区分（INSURANCE_TYPE.LIFE | INSURANCE_TYPE.SONPO）
 * @param {Array<{id: number, name: string, insurance_type: number}>} props.insuranceCompanies - 保険会社マスター一覧
 * @param {number[]} props.selectedIds - 選択中の保険会社IDリスト
 * @param {Function} props.onChange - 選択変更コールバック。引数: (newIds: number[])
 */
const InsuranceCompanySelect = ({
  insuranceType,
  insuranceCompanies,
  selectedIds,
  onChange,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const fieldRef = useRef(null);

  // 現在の生損保区分に合致する会社だけを絞り込む（生損保混在リストから対象種別のみ表示）
  const filtered = insuranceCompanies.filter(c => Number(c.insurance_type) === insuranceType);
  const typeName = getInsuranceTypeName(insuranceType);
  // filtered が空のとき「全選択」と誤認しないよう length > 0 でガードする
  const isAllSelected = filtered.length > 0 && filtered.every(c => selectedIds.includes(c.id));

  // UI 表示用に選択中の会社名を取得（filtered 内に存在しないIDは除外）
  const selectedNames = filtered
    .filter(c => selectedIds.includes(c.id))
    .map(c => c.name);

  const togglePopup = () => setShowPopup(prev => !prev);

  const handleToggle = (id) => {
    // スプレッドでイミュータブルに配列を更新する
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(v => v !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    // 全選択済みなら全解除、未全選択なら filtered の全 ID を選択
    onChange(isAllSelected ? [] : filtered.map(c => c.id));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fieldRef.current && !fieldRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderDisplayText = () => {
    if (selectedIds.length === 0) {
      return '指定なし';
    }
    return (
      <div className="dc-selected-list">
        {/* UI スペース制約により表示は最大 5 件。超過分は「他N件」で集約 */}
        {selectedNames.slice(0, 5).map((name, i) => (
          <div key={i} className="dc-selected-item">{name}</div>
        ))}
        {selectedNames.length > 5 && (
          <div className="dc-selected-item">他{selectedNames.length - 5}件</div>
        )}
      </div>
    );
  };

  return (
    <div className="dc-popup-field" ref={fieldRef}>
      <div className="dc-trigger" onClick={togglePopup}>
        <span className="dc-trigger-label">
          {typeName}会社
          <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 3V14H11" stroke="#0073BC" strokeWidth="2"/>
            <rect x="5" y="1" width="8" height="9" fill="white" stroke="#0073BC" strokeWidth="2"/>
          </svg>
        </span>
        <span className="dc-trigger-value">{renderDisplayText()}</span>
      </div>
      {showPopup && (
        <div className="dc-popup dc-popup--company">
          <div className="dc-popup-header">
            <span>{typeName}会社選択</span>
            <div className="dc-popup-header-actions">
              <button type="button" className="dc-popup-close-btn" onClick={togglePopup}>
                選択完了
              </button>
            </div>
          </div>
          <div className="dc-popup-body dc-checkbox-grid">
            {filtered.length > 0 && (
              <div className="dc-select-all-row">
                <label className="checkbox-native-box">
                  <input
                    className="checkbox-native"
                    type="checkbox"
                    id="dc-company-select-all"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </label>
                <label htmlFor="dc-company-select-all">全て選択</label>
              </div>
            )}
            {filtered.length === 0 ? (
              <p className="dc-popup-empty">選択可能な{typeName}会社がありません。</p>
            ) : (
              filtered.map(c => {
                const cbId = `dc-company-${insuranceType}-${c.id}`;
                return (
                  <div key={c.id} className="dc-checkbox-item">
                    <label className="checkbox-native-box">
                      <input
                        className="checkbox-native"
                        type="checkbox"
                        id={cbId}
                        checked={selectedIds.includes(c.id)}
                        onChange={() => handleToggle(c.id)}
                      />
                    </label>
                    <label htmlFor={cbId}>{c.name}</label>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(InsuranceCompanySelect);
